// The dashboard's shortcut grid — a fixed 2x2, never a 4-across strip.
//
// The 4 cards differ by persona, not just by which two the top row already
// covers (see FocusCards):
//   • CSOC     — the top row is Class + Assignment, so Play + Fun Challenge
//                sit here instead, next to a single consolidated Trainers
//                card (Positions is its own card either way).
//   • non-CSOC — Play + Fun Challenge are already promoted to the top row, so
//                this grid unpacks Trainers back into its three individual
//                drills (Calculation, Intuition, Square) alongside Positions
//                — nothing is hidden behind a single tile for this persona.

import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import MaterialCommunityIcon from 'react-native-vector-icons/MaterialCommunityIcons';
import {
  CalculationTrainerCard,
  FunChallengeCard,
  IntuitionTrainerCard,
  PlayCard,
  PositionsCard,
  SquareTrainerCard,
  TrainersCard,
} from '../QuickActions';
import {radii} from '@/Utils/radius';

interface TrainAndSolveGridProps {
  navigation: any;
  /** CSOC students keep Play + Fun Challenge here; everyone else has them above. */
  isCsoc?: boolean;
}

function TrainAndSolveGrid({navigation, isCsoc}: TrainAndSolveGridProps) {
  const [width, setWidth] = useState(0);
  const stacked = width > 0 && width < 420;

  return (
    <View
      style={styles.section}
      onLayout={e => setWidth(e.nativeEvent.layout.width)}>
      <View style={styles.header}>
        <View style={styles.headerIcon}>
          <MaterialCommunityIcon
            name="dumbbell"
            size={19}
            color={CCColors.Primary.Brown}
          />
        </View>
        <View style={{flex: 1, minWidth: 0}}>
          <CCText
            fontSize={17}
            fontWeight="bold"
            color={CCColors.TextColor.Dark}>
            {isCsoc ? 'Train & play' : 'Train & solve'}
          </CCText>
          <CCText
            fontSize={12.5}
            color={CCColors.TextColor.Grey}
            style={{marginTop: 2}}>
            {isCsoc
              ? 'Sharpen a skill or jump straight into a game'
              : 'Sharpen a different skill every day'}
          </CCText>
        </View>
      </View>

      {/* Rows rather than one wrapping container: alignItems:'stretch' only
          matches heights within the same row, so a single wrapped container
          couldn't guarantee one row's pair matches each other's height
          independently of the other row's pair matching theirs. */}
      {isCsoc ? (
        <>
          <View style={[styles.row, stacked && styles.rowStacked]}>
            <PlayCard navigation={navigation} stacked={stacked} />
            <FunChallengeCard navigation={navigation} stacked={stacked} />
          </View>
          <View
            style={[
              styles.row,
              stacked && styles.rowStacked,
              styles.rowSpaced,
            ]}>
            <PositionsCard navigation={navigation} stacked={stacked} />
            <TrainersCard navigation={navigation} stacked={stacked} />
          </View>
        </>
      ) : (
        <>
          <View style={[styles.row, stacked && styles.rowStacked]}>
            <PositionsCard navigation={navigation} stacked={stacked} />
            <CalculationTrainerCard navigation={navigation} stacked={stacked} />
          </View>
          <View
            style={[
              styles.row,
              stacked && styles.rowStacked,
              styles.rowSpaced,
            ]}>
            <IntuitionTrainerCard navigation={navigation} stacked={stacked} />
            <SquareTrainerCard navigation={navigation} stacked={stacked} />
          </View>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    backgroundColor: CCColors.White,
    borderRadius: radii.panel,
    padding: 18,
    shadowColor: CCColors.Primary.Dark,
    shadowRadius: 8,
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.05,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 14,
  },
  headerIcon: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: CCColors.Primary.lightBrown,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 10,
  },
  rowStacked: {flexDirection: 'column'},
  rowSpaced: {marginTop: 10},
});

export default TrainAndSolveGrid;
